import React, { useMemo, useState } from "react";
import ScrollModal from "@/components/common/scroll-modal";
import {
  TextField,
  Icon,
  Button,
  Heading,
  GroupTextField,
  Input,
  Badge,
  Radiobutton,
  GroupTextFieldRow,
  Switch,
} from "@elaniintc/goes-design-system";
import GroupTextFieldFormDesign from "./GroupTextFieldFormDesign";
import SwitchField from "./SwitchField";
import SelectField from "@/components/common/select";
import InfoMessage from "./InfoMessage";
import { DateInput } from "./DateInput";
import { OptionTooltip } from "./OptionTooltip";
import type { FormField, FormPage } from "../types";
import { DropdownListSubMenu } from "@/components/ui/dropdown-list/dropdown-list-submenu";

import TimeInput from "./TimeInput";
import type { Variable } from "./VisibilityCondition/types";
import type { Variable as VariableType } from "../../types";
import { useGetVariablesDataCenter } from "@/workflow_api/hooks/variables/useGetVariables";
import {
  DropdownList,
  type DropdownListOptions,
} from "@/components/ui/dropdown-list/dropdown-list";
import { parseCharacters } from "@/utils/format";
import dayjs from "dayjs";
import { Renderify } from "renderify-js";
import DefaultValueField, {
  type InteroperabilityVariable,
} from "./DefaultValueField";
import VisibilitySection from "./VisibilitySection";
import InteroperabilityConfig, {
  type InteroperabilityConfigReturn,
} from "./InteroperabilityConfig";
import { cn } from "@/utils/theme";

interface FieldConfigModalProps {
  open: boolean;
  type: string | null;
  sectionId: string | null;
  initialField?: FormField;
  onClose: () => void;
  onSave: (data: FormField) => void;
  formData?: FormPage[];
}

const fieldTypeLabels: Record<string, string> = {
  text: "Texto",
  number: "Números",
  textarea: "Área de texto",
  date: "Fecha y hora",
  datetime: "Fecha y hora",
  datetime_range: "Rango de fechas",
  date_range: "Rango de fechas",
  select: "Lista de selección",
  email: "Correo electrónico",
  phone: "Número telefónico",
  file: "Subida de archivos",
  identification: "Identificación",
  interoperability: "Interoperabilidad",
  switch: "Campo Switch",
};

const NUMERIC_FORMAT_OPTIONS = [
  {
    label: "Normal",
    detail: "num",
    value: "normal",
    icon: { type: "far", name: "hashtag" },
  },
  {
    label: "Porcentaje",
    detail: "num",
    value: "percentage",
    icon: { type: "far", name: "percent" },
  },
  {
    label: "Dólar Estadounidense",
    detail: "USD",
    value: "usd",
    icon: { type: "far", name: "dollar-sign" },
  },
  {
    label: "Euro",
    detail: "EUR",
    value: "eur",
    icon: { type: "far", name: "euro-sign" },
  },
  {
    label: "Yen Japonés",
    detail: "JPY",
    value: "jpy",
    icon: { type: "far", name: "yen-sign" },
  },
] as const;

type IconSize = "xs" | "sm" | "lg" | "xl" | "2xl";
type VariableRef = {
  groupId: string;
  groupLabels: string[];
  itemValue: string;
  itemLabel: string;
  groupIcon?: {
    type: string;
    name: string;
    size?: IconSize;
    className?: string;
  };
  itemIcon?: {
    type: string;
    name: string;
    size?: IconSize;
    className?: string;
  };
  type: string;
  source?: string;
} | null;

// Helper para validar si el variableRef viene "completo" (select de tipo centro de datos)
const isCompleteVariableRef = (
  vr: VariableRef | undefined | null,
): vr is Exclude<VariableRef, null> => {
  if (!vr) return false;

  const hasItem =
    typeof vr.itemValue === "string" &&
    vr.itemValue.trim() !== "" &&
    typeof vr.itemLabel === "string" &&
    vr.itemLabel.trim() !== "";

  const hasGroup =
    typeof vr.groupId === "string" &&
    vr.groupId.trim() !== "" &&
    Array.isArray(vr.groupLabels) &&
    vr.groupLabels.length > 0;

  return hasItem && hasGroup;
};

type ConditionData = {
  selectedValue?: {
    id?: string;
    name?: string;
    type?: string;
  };
  selectedOperation?: {
    name: string;
    label: string;
    needsComparativeValue: boolean;
    comparativeValueType: string;
  };
  compareValueType?: string;
  compareInput?: string;
  compareDate?: string | Date;
  selectedVar?: {
    id: string;
    name: string;
    type: string;
  };
};

// --- Utils

const deepCloneConditions = (
  arr: Array<{ id: number | string; data: ConditionData }>,
): Array<{ id: number | string; data: ConditionData }> =>
  arr.map((c) => ({
    id: c.id,
    data: { ...(c.data ?? {}) },
  }));

type IconDef = {
  type: "far" | "fas" | "fal" | "fab";
  name: string;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
};

const LIST_ICON: IconDef = { type: "far", name: "list" };
const COL_ICON: IconDef = { type: "far", name: "columns" };

type DataCenterTables = Array<{
  nodeId: string;
  nodeName: string;
  nodeType: string;
  source: string | null;
  tables: Array<{
    name: string;
    columns: Array<{ id: string; name: string; type: string }>;
  }>;
}>;

type DropdownItem = {
  label?: string;
  detail?: string;
  value: string | number;
  icon?: IconDef;
};

type DropdownGroup = {
  groupId: string;
  groupLabels: string[];
  icon?: IconDef;
  items: DropdownItem[];
  source?: string;
};

const TABLE_ICON: IconDef = {
  type: "far",
  name: "table",
  size: "xs",
  className: "text-info-quiet",
};

/**
 * Mapea las columnas para el submenú (segundo nivel).
 * - Cada combinación Nodo + Tabla es un grupo del submenú
 * - Cada columna es un item dentro del grupo
 */
function toSubmenuOptions(data: DataCenterTables): DropdownGroup[] {
  const groups: DropdownGroup[] = [];
  data.forEach((node) => {
    node.tables.forEach((table) => {
      groups.push({
        groupId: node.nodeId,
        groupLabels: [node.nodeName, table.name],
        icon: TABLE_ICON,
        source: node.source || "sdt",
        items: table.columns
          .filter(
            (col) => !["id", "created_at", "updated_at"].includes(col.name),
          )
          .map((col) => ({
            label: col.name,
            value: col.id,
            icon: COL_ICON,
            type: col.type,
          })),
      });
    });
  });

  return groups;
}

/**
 * Mapea los nodos y sus tablas a las `options` del primer nivel.
 * - Cada nodo es un grupo
 * - Cada tabla es un item dentro del grupo
 */
const toOptions = (data: DataCenterTables): DropdownGroup[] => {
  return data.map((node) => ({
    groupId: node.nodeId,
    groupLabels: [node.nodeName],
    icon: TABLE_ICON,
    items: node.tables.map((table) => ({
      label: table.name,
      detail: "table",
      value: table.name,
      icon: LIST_ICON,
    })),
  }));
};

/* ========= Helpers para validación de número/decimales ========= */

const clampByMaxLength = (raw: string, maxLen?: number) => {
  if (!maxLen || Number.isNaN(maxLen)) return raw;
  return raw.length <= maxLen ? raw : raw.slice(0, maxLen);
};

const buildTypingRegex = (precision: number | null) => {
  // Permite escritura progresiva (0..N decimales) pero nunca más de los permitidos
  if (precision === null) {
    // Automático: hasta 4 decimales, decimales opcionales
    return /^\d*(?:\.\d{0,4})?$/;
  }
  if (precision === 0) {
    // Solo enteros
    return /^\d*$/;
  }
  // N decimales máximo mientras escribe
  const re = new RegExp(`^\\d*(?:\\.\\d{0,${precision}})?$`);
  return re;
};

const buildStrictRegex = (precision: number | null) => {
  // Para validación final cuando hay valor (no vacío)
  if (precision === null) {
    // Automático: entero o con 1–4 decimales
    return /^\d+(?:\.\d{1,4})?$/;
  }
  if (precision === 0) {
    return /^\d+$/;
  }
  const re = new RegExp(`^\\d+\\.\\d{${precision}}$`);
  return re;
};

const minCharsForPrecision = (precision: number | null) => {
  if (precision && precision > 0) return precision + 2; // ej: 1.23 => 4
  return 1; // con 0 decimales al menos 1 dígito
};

const normalizeRef = (s?: string | null) =>
  parseCharacters((s ?? "").toString().trim());

/* ========================== END Helpers =============================== */

const FieldConfigModal: React.FC<FieldConfigModalProps> = ({
  open,
  type,
  sectionId,
  onClose,
  onSave,
  initialField,
  formData,
}) => {
  function extractFields(
    fd?: FormPage[],
    currentField?: { id?: string | null; name?: string },
  ) {
    if (!fd) return [] as VariableType[];
    const result: VariableType[] = [];
    let stop = false;

    for (const page of fd) {
      if (stop) break;

      for (const section of page.sections ?? []) {
        if (stop) break;

        const isTargetSection = section.id === sectionId;

        for (const field of section.fields ?? []) {
          // Usar el ID único del campo para identificar el campo actual
          // Esto asegura que funcione correctamente aunque haya campos con el mismo nombre
          const isCurrentField = currentField?.id
            ? field.id === currentField.id
            : currentField?.name && field.name === currentField.name;

          if (isCurrentField) {
            stop = true;
            break;
          }

          if (!stop) {
            // Usar technicalKey como identificador único, con fallback a name
            const fieldKey = field.technicalKey || field.name;
            result.push({
              id: fieldKey,
              name: field.name,
              type: field.type as VariableType["type"],
            });
          }
        }
        if (!currentField && isTargetSection) {
          stop = true;
          break;
        }
      }
    }

    return result;
  }

  function extractInteroperabilityOutputs() {
    const allOutputValues: InteroperabilityVariable[] = [];
    if (!formData) return allOutputValues;

    for (const page of formData) {
      for (const section of page.sections ?? []) {
        for (const field of section.fields ?? []) {
          if (field.output_values) {
            allOutputValues.push(...field.output_values);
          }
        }
      }
    }
    return allOutputValues;
  }

  const interoperabilityVariables = extractInteroperabilityOutputs();

  const [isConditional, setIsConditional] = React.useState(false);
  const [conditions, setConditions] = React.useState<
    Array<{ id: number | string; data: ConditionData }>
  >([{ id: Date.now(), data: {} }]);
  const [conditionsOperator, setConditionsOperator] = React.useState<
    "OR" | "AND"
  >("OR");

  //centro de datos

  const { tables: dataCenterTables } = useGetVariablesDataCenter();

  const options = useMemo(
    () => toOptions(dataCenterTables),
    [dataCenterTables],
  );
  const submenuOptions = useMemo(
    () => toSubmenuOptions(dataCenterTables),
    [dataCenterTables],
  );
  // console.log(submenuOptions);

  // states
  const [fieldName, setFieldName] = useState("");
  const [keyValue, setKeyValue] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [required, setRequired] = useState(false);
  const [preventable, setPreventable] = useState(false);

  const [precision, setPrecision] = useState<number | null>(null);
  const [numberFormat, setNumberFormat] = useState<
    "normal" | "percentage" | "usd" | "eur" | "jpy"
  >("normal");

  const [configDefaultValue, setConfigDefaultValue] = useState("");
  const [configDefaultTimeValue, setConfigDefaultTimeValue] = useState("");

  const [configDefaultStartDate, setConfigDefaultStartDate] = useState("");
  const [configDefaultEndDate, setConfigDefaultEndDate] = useState("");
  const [configDefaultStartTime, setConfigDefaultStartTime] = useState("");
  const [configDefaultEndTime, setConfigDefaultEndTime] = useState("");
  const [minValue, setMinValue] = useState<number | undefined>(undefined);
  const [maxValue, setMaxValue] = useState<number | undefined>(undefined);

  // Estados para el valor por defecto tipo inp/var/cam
  const [defaultValueType, setDefaultValueType] = useState<
    "inp" | "var" | "cam"
  >("inp");
  const [defaultValueVariable, setDefaultValueVariable] =
    useState<VariableType | null>(null);

  const [configMaxLength, setConfigMaxLength] = useState("");
  const [configMinDate, setConfigMinDate] = useState("");
  const [configMaxDate, setConfigMaxDate] = useState("");
  const [configAllowedDomains, setConfigAllowedDomains] = useState("");
  const [configAllowedDomainsList, setConfigAllowedDomainsList] = useState<
    string[]
  >([]);
  const [configDisallowedDomains, setConfigDisallowedDomains] = useState("");
  const [configDisallowedDomainsList, setConfigDisallowedDomainsList] =
    useState<string[]>([]);
  const [configAllowCountry, setConfigAllowCountry] = useState(false);
  const [configAllowAnyFile, setConfigAllowAnyFile] = useState(false);
  const [configAllowedFilesList, setConfigAllowedFilesList] = useState<
    string[]
  >([]);
  const [configAllowMultiple, setConfigAllowMultiple] = useState(false);
  const [configIsVarByRegistry, setConfigIsVarByRegistry] = useState(false);
  const [configIncludeTime, setConfigIncludeTime] = useState(false);
  const [config24HourFormat, setConfig24HourFormat] = useState(false);
  const [configAllowUserChooseDocType, setConfigAllowUserChooseDocType] =
    useState(false);
  const [configDocumentType, setConfigDocumentType] = useState("");
  const [configAllowedFilesInput, setConfigAllowedFilesInput] = useState("");

  const [configSelectVariable, setConfigSelectVariable] =
    useState<VariableRef>(null);

  const [configSelectSource, setConfigSelectSource] = useState<
    "database" | "manual"
  >("database");
  const [configSelectItems, setConfigSelectItems] = useState<string[]>([]);
  const [configSelectNewItem, setConfigSelectNewItem] = useState<string>("");
  const [configSelectAllowMultiple, setConfigSelectAllowMultiple] =
    useState<boolean>(false);

  const [maxFileSize, setMaxFileSize] = useState("");

  const [fileSizeKey, setFileSizeKey] = useState(true);

  // Interoperability states
  const [configInteropInstitution, setConfigInteropInstitution] = useState("");
  const [configInteropAction, setConfigInteropAction] = useState("");
  const [configInteropParams, setConfigInteropParams] = useState<
    Array<{ name: string; type: string; label: string; placeholder: string }>
  >([]);
  const [configInteropReturns, setConfigInteropReturns] = useState<
    InteroperabilityConfigReturn[]
  >([]);

  // Switch states
  const [configSwitchLabel, setConfigSwitchLabel] = useState("");

  const onlyLettersRegex = /^[a-zA-Z]$/;
  const ALLOWED_EXTENSIONS = new Set([
    "pdf",
    "doc",
    "docx",
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "svg",
    "txt",
    "csv",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
    "json",
    "zip",
    "rar",
    "mpp",
    "mpt",
    "dwg",
    "dxf",
    "dwf",
  ]);

  const value = configAllowedFilesInput.trim().toLowerCase();
  const isValidExtension =
    value.length > 0 &&
    ALLOWED_EXTENSIONS.has(value) &&
    !configAllowedFilesList.includes(value);

  const [isReadOnly, setIsReadOnly] = useState(false);
  const [numericFormatSearch, setNumericFormatSearch] = useState("");

  const [mask, setMask] = useState("");

  // end states

  const handleAddSelectItem = () => {
    const v = configSelectNewItem.trim();
    if (!v) return;
    setConfigSelectItems((prev) => [...prev, v]);
    setConfigSelectNewItem("");
  };

  const handleRemoveSelectItem = (index: number) => {
    setConfigSelectItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleSelectMultiple = (
    valueOrEvent: boolean | React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (typeof valueOrEvent === "boolean") {
      setConfigSelectAllowMultiple(valueOrEvent);
    } else if (
      "target" in valueOrEvent &&
      typeof valueOrEvent.target.checked === "boolean"
    ) {
      setConfigSelectAllowMultiple(valueOrEvent.target.checked);
    } else {
      setConfigSelectAllowMultiple((prev) => !prev);
    }
  };

  const selectValue: string =
    precision === null || precision === undefined ? "auto" : String(precision);

  const handleChange = (value: string) => {
    if (value === "auto") {
      setPrecision(null);
    } else {
      setPrecision(Number(value));
    }
    // --- Borrar el valor por defecto
    setConfigDefaultValue("");
  };

  // validación de condiciones
  const [validById, setValidById] = useState<Record<string, boolean>>({});
  const handleValidityChange =
    (id: string) =>
    (isValid: boolean): void => {
      setValidById((prev) =>
        prev[id] === isValid ? prev : { ...prev, [id]: isValid },
      );
    };

  // Guardar solo si TODOS son válidos
  const canSaveAll =
    conditions.length > 0 &&
    conditions.every((it) => validById[String(it.id)] === true);

  const isFormValid = () => {
    if (type === "select" && configSelectSource === "database") {
      if (!configSelectVariable) return false;
    }

    // Check basic required fields
    if (!fieldName.trim()) return false;
    if (
      !(
        type === "datetime" ||
        type === "date" ||
        type === "datetime_range" ||
        type === "date_range" ||
        type === "identification" ||
        type === "file"
      ) &&
      !placeholder.trim()
    )
      return false;

    if (
      configMinDate &&
      configMaxDate &&
      configMinDate !== "now" &&
      configMaxDate !== "now" &&
      dayjs(configMinDate, "DD/MM/YYYY").isAfter(
        dayjs(configMaxDate, "DD/MM/YYYY"),
      )
    ) {
      return false;
    }

    if (["text", "textarea", "email"].includes(type || "") && !configMaxLength)
      return false;

    // --- validaciones adicionales cuando type === "number"
    if (type === "number") {
      // Si hay valor por defecto, validar formato estricto según precisión
      if (configDefaultValue !== "") {
        const strictRe = buildStrictRegex(precision);

        if (!strictRe.test(configDefaultValue)) return false;

        const numericValue = parseFloat(configDefaultValue);

        if (!Number.isFinite(numericValue)) return false;

        if (minValue != null && numericValue < minValue) return false;
        if (maxValue != null && numericValue > maxValue) return false;
      }
    }

    if (
      type === "identification" &&
      !configAllowUserChooseDocType &&
      !configDocumentType
    )
      return false;
    if (
      type === "file" &&
      !configAllowAnyFile &&
      configAllowedFilesList.length === 0
    )
      return false;

    if (type === "select" && configSelectSource === "manual") {
      if (configSelectItems.length === 0) return false;
    }

    if (!canSaveAll && isConditional) {
      return false;
    }

    if (type === "datetime" || type === "date") {
      if (
        configDefaultTimeValue &&
        !config24HourFormat &&
        !configDefaultTimeValue.includes("AM") &&
        !configDefaultTimeValue.includes("PM")
      ) {
        return false;
      }
    }

    if (type === "datetime_range" || type === "date_range") {
      if (
        configDefaultStartTime &&
        !config24HourFormat &&
        !configDefaultStartTime.includes("AM") &&
        !configDefaultStartTime.includes("PM")
      ) {
        return false;
      }
      if (
        configDefaultEndTime &&
        !config24HourFormat &&
        !configDefaultEndTime.includes("AM") &&
        !configDefaultEndTime.includes("PM")
      ) {
        return false;
      }
      // Validate that start date is not after end date
      if (
        configDefaultStartDate &&
        configDefaultEndDate &&
        dayjs(configDefaultStartDate, "DD/MM/YYYY").isAfter(
          dayjs(configDefaultEndDate, "DD/MM/YYYY"),
        )
      ) {
        return false;
      }
    }

    if (type === "email" && typeof configDefaultValue === "string") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (configDefaultValue && !emailRegex.test(configDefaultValue)) {
        return false;
      }
    }

    if (type === "file" && maxFileSize === "") {
      return false;
    }

    if (type === "file" && Number(maxFileSize) <= 0) {
      return false;
    }

    if (type === "interoperability") {
      if (!configInteropInstitution || !configInteropAction) {
        return false;
      }
    }

    if (type === "switch") {
      if (!configSwitchLabel || !configSwitchLabel.trim()) {
        return false;
      }
    }

    if (type === "number") {
      if (
        minValue !== undefined &&
        maxValue !== undefined &&
        minValue > maxValue
      ) {
        return false;
      }
    }

    return true;
  };

  const lastInitIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (open) {
      const initId = initialField?.id ?? "__new__";
      if (initialField && lastInitIdRef.current === initId) return;
      lastInitIdRef.current = initId;

      if (initialField) {
        // clones
        setFieldName(initialField.name || "");
        setKeyValue(initialField.technicalKey || "");
        setPlaceholder(initialField.placeholder || "");
        setRequired(!!initialField.required);
        setPreventable(
          initialField.isPreventable === undefined
            ? true
            : !!initialField.isPreventable,
        );
        setConfigMaxLength(
          initialField.maxLength ? String(initialField.maxLength) : "",
        );
        setConfigMinDate(initialField.minDate || "");
        setConfigMaxDate(initialField.maxDate || "");
        setConfigAllowedDomains("");
        setConfigAllowedDomainsList([...(initialField.allowedDomains ?? [])]);
        setConfigDisallowedDomainsList([
          ...(initialField.disallowedDomains ?? []),
        ]);
        setConfigAllowCountry(!!initialField.allowCountry);
        setConfigAllowAnyFile(!!initialField.allowAnyFile);
        setConfigAllowedFilesList([...(initialField.fileTypes ?? [])]);
        setConfigAllowMultiple(!!initialField.multipleFiles);
        setConfigIncludeTime(!!initialField.includeTime);
        setConfig24HourFormat(!!initialField.format24Hour);
        setConfigAllowUserChooseDocType(!!initialField.allowUserChooseDocType);
        setConfigDocumentType(initialField.documentType || "");
        setConfigAllowedFilesInput("");

        setConfigSelectSource(
          initialField.valueSource === "manual" ? "manual" : "database",
        );
        setConfigIsVarByRegistry(!!initialField.is_var_by_registry);
        setConfigSelectItems(
          Array.isArray(initialField.options) ? [...initialField.options] : [],
        );
        setConfigSelectAllowMultiple(!!initialField.multiple);
        setConfigSelectNewItem("");

        // 🔹 Aquí limpiamos el dropdown si el valor por defecto no viene completo
        const iv = initialField?.variableRef as VariableRef | undefined;
        if (isCompleteVariableRef(iv)) {
          setConfigSelectVariable({ ...iv });
        } else {
          setConfigSelectVariable(null);
        }

        setIsConditional(!!initialField.isConditional);
        setConditions(
          initialField.conditions
            ? deepCloneConditions(initialField.conditions)
            : [{ id: Date.now(), data: {} }],
        );
        setConditionsOperator(initialField.conditionsOperator || "OR");
        setConfigDefaultValue(initialField.configDefaultValue || "");
        setConfigDefaultTimeValue(initialField.configDefaultTimeValue || "");
        setConfigDefaultStartDate(initialField.configDefaultStartDate || "");
        setConfigDefaultStartTime(initialField.configDefaultStartTime || "");
        setConfigDefaultEndDate(initialField.configDefaultEndDate || "");
        setConfigDefaultEndTime(initialField.configDefaultEndTime || "");

        // ---- cargar precision si existe; si no, null
        setPrecision(
          typeof initialField?.precision === "number"
            ? (initialField.precision as number)
            : null,
        );
        setMaxValue(initialField.maxValue as number | undefined);
        setMinValue(initialField.minValue as number | undefined);
        setNumberFormat(initialField.numberFormat ?? "normal");

        setMaxFileSize(initialField.maxFileSize || "");
        setFileSizeKey(initialField.fileSizeKey || false);

        // Load default value configuration
        const loadedType = initialField.defaultValueType;
        setDefaultValueType(loadedType || "inp");
        setDefaultValueVariable(initialField.defaultValueVariable || null);

        // Load interoperability configuration
        setConfigInteropInstitution(
          (initialField as FormField & { interopInstitution?: string })
            ?.service ||
            (initialField as FormField & { interopInstitution?: string })
              ?.interopInstitution ||
            "",
        );
        setConfigInteropAction(
          (initialField as FormField & { interopAction?: string })?.action ||
            (initialField as FormField & { interopAction?: string })
              ?.interopAction ||
            "",
        );

        setConfigInteropParams(
          (initialField.input_field as Array<{
            name: string;
            type: string;
            label: string;
            placeholder: string;
          }>) ||
            (initialField.interop_params as Array<{
              name: string;
              type: string;
            }>) ||
            [],
        );
        setConfigInteropReturns(
          (initialField.output_values as InteroperabilityConfigReturn[]) || [],
        );

        // Load switch configuration
        setConfigSwitchLabel(initialField.label || "");

        setIsReadOnly(initialField.isReadOnly || false);

        setMask(initialField.mask || "");
      } else {
        // Reset total para crear nuevo
        setFieldName("");
        setPlaceholder("");
        setRequired(false);
        setPreventable(true);
        setConfigMaxLength("");
        setConfigMinDate("");
        setConfigMaxDate("");
        setConfigAllowedDomains("");
        setConfigAllowedDomainsList([]);
        setConfigDisallowedDomainsList([]);
        setConfigAllowCountry(false);
        setConfigAllowAnyFile(false);
        setConfigAllowedFilesList([]);
        setConfigAllowMultiple(false);
        setConfigIsVarByRegistry(false);
        setConfigIncludeTime(false);
        setConfig24HourFormat(false);
        setConfigAllowUserChooseDocType(false);
        setConfigDocumentType("");
        setConfigAllowedFilesInput("");

        setConfigSelectSource("database");
        setConfigSelectItems([]);
        setConfigSelectAllowMultiple(false);
        setConfigSelectNewItem("");

        setConfigSelectVariable(null);

        setIsConditional(false);
        setConditions([{ id: Date.now(), data: {} }]);
        setConditionsOperator("OR");
        setConfigDefaultValue("");
        setConfigDefaultTimeValue("");
        setConfigDefaultStartDate("");
        setConfigDefaultStartTime("");
        setConfigDefaultEndDate("");
        setConfigDefaultEndTime("");

        setPrecision(null);
        setMaxValue(undefined);
        setMinValue(undefined);
        setNumberFormat("normal");
        setMaxFileSize("");
        setFileSizeKey(false);

        setDefaultValueType("inp");
        setDefaultValueVariable(null);

        setConfigInteropInstitution("");
        setConfigInteropAction("");
        setConfigInteropParams([]);
        setConfigInteropReturns([]);

        // Reset switch configuration
        setConfigSwitchLabel("");

        setIsReadOnly(false);

        setMask("");

        // Initialize default value for switch fields
        if (type === "switch") {
          setConfigDefaultValue("false");
        }
      }
    }
  }, [open, type, initialField]);

  const generateId = (): string => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return `field_${crypto.randomUUID()}`;
    }
    return `field_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  const handleSave = () => {
    if (!type || !sectionId) return;

    const clonedConditions = deepCloneConditions(conditions);
    const fieldData: FormField = {
      id: initialField?.id ?? generateId(),
      type: type as FormField["type"],
      name: fieldName,
      technicalKey: keyValue || parseCharacters(fieldName.toLowerCase()),
      placeholder: placeholder || "",
      required,
      isPreventable: preventable,
      maxLength: configMaxLength ? Number(configMaxLength) : undefined,
      minDate: configMinDate || undefined,
      maxDate: configMaxDate || undefined,
      allowedDomains:
        configAllowedDomainsList.length > 0
          ? [...configAllowedDomainsList]
          : undefined,
      disallowedDomains:
        configDisallowedDomainsList.length > 0
          ? [...configDisallowedDomainsList]
          : undefined,
      fileTypes:
        configAllowedFilesList.length > 0
          ? [...configAllowedFilesList]
          : undefined,
      multipleFiles: configAllowMultiple,
      includeTime: configIncludeTime,
      format24Hour: config24HourFormat,

      ...(type === "identification" && {
        allowUserChooseDocType: configAllowUserChooseDocType,
        documentType: configDocumentType,
      }),
      ...(type === "phone" && {
        allowCountry: configAllowCountry,
      }),
      ...(type === "file" && {
        allowAnyFile: configAllowAnyFile,
        maxFileSize: maxFileSize,
        fileSizeKey: fileSizeKey,
      }),

      ...(type === "select" && {
        valueSource: configSelectSource, // "database" | "manual"
        is_var_by_registry: configIsVarByRegistry,
        options:
          configSelectSource === "manual" ? [...configSelectItems] : undefined,
        multiple: configSelectAllowMultiple,
        ...(configSelectSource === "database" && configSelectVariable
          ? { variableRef: { ...configSelectVariable } }
          : {}),
      }),

      ...(type === "interoperability" && {
        service: configInteropInstitution,
        action: configInteropAction,
        input_field: configInteropParams,
        interopInstitution: configInteropInstitution,
        interopAction: configInteropAction,
        interopParams: configInteropParams,
        output_values: configInteropReturns,
      }),

      ...(type === "switch" && {
        label: configSwitchLabel,
      }),

      ...(type === "number" && { precision, minValue, maxValue, numberFormat }),

      configDefaultValue,
      configDefaultTimeValue,
      configDefaultStartDate,
      configDefaultStartTime,
      configDefaultEndDate,
      configDefaultEndTime,

      // Default value configuration
      defaultValueType,
      defaultValueVariable:
        defaultValueType === "var" || defaultValueType === "cam"
          ? defaultValueVariable || undefined
          : undefined,

      // condicional
      isConditional,
      ...(isConditional && {
        conditions: clonedConditions,
        conditionsOperator,
      }),

      isReadOnly,

      ...(type === "text" && { mask: mask || undefined }),
    };

    onSave(fieldData);
  };

  const handleClose = () => {
    // Reset todo
    setFieldName("");
    setPlaceholder("");
    setRequired(false);
    setPreventable(true);
    setConfigMaxLength("");
    setConfigMinDate("");
    setConfigMaxDate("");
    setConfigAllowedDomains("");
    setConfigAllowedDomainsList([]);
    setConfigDisallowedDomainsList([]);
    setConfigAllowCountry(false);
    setConfigAllowAnyFile(false);
    setConfigAllowedFilesList([]);
    setConfigAllowMultiple(false);
    setConfigIncludeTime(false);
    setConfig24HourFormat(false);
    setConfigAllowUserChooseDocType(false);
    setConfigDocumentType("");
    setConfigAllowedFilesInput("");

    setConfigSelectSource("database");
    setConfigSelectItems([]);
    setConfigSelectAllowMultiple(false);
    setConfigSelectNewItem("");

    setConfigSelectVariable(null);

    setIsConditional(false);
    setConditions([{ id: Date.now(), data: {} }]);
    setConditionsOperator("OR");

    setConfigDefaultValue("");
    setConfigDefaultTimeValue("");

    setConfigDefaultStartDate("");
    setConfigDefaultStartTime("");
    setConfigDefaultEndDate("");
    setConfigDefaultEndTime("");

    setPrecision(null);
    setMaxFileSize("");
    setFileSizeKey(false);

    // Reset default value configuration
    setDefaultValueType("inp");
    setDefaultValueVariable(null);

    // Reset interoperability
    setConfigInteropInstitution("");
    setConfigInteropAction("");
    setConfigInteropParams([]);
    setConfigInteropReturns([]);

    // Reset switch configuration
    setConfigSwitchLabel("");

    // Reset isReadOnly
    setIsReadOnly(false);
    setMinValue(undefined);
    setMaxValue(undefined);
    setNumberFormat("normal");

    setMask("");

    setMask("");

    onClose();
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] h-full max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            {type ? fieldTypeLabels[type] || "Campo" : "Campo"}
          </Heading>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-sm text-body" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="button"
            className="min-w-35 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={!isFormValid()}
            onClick={handleSave}
          >
            Guardar
          </Button>
        </div>
      }
    >
      <div className="px-8.5 space-y-6 overflow-y-auto text-sm">
        {/* Shared section */}
        <Heading variant="h6" className="mb-4">
          Detalles
        </Heading>
        <GroupTextFieldFormDesign
          label="Nombre del campo"
          required
          value={fieldName}
          onChange={setFieldName}
          keyValue={keyValue}
          onKeyChange={setKeyValue}
          canChangeKey={true}
        />

        <Renderify
          condition={
            !(
              type === "datetime" ||
              type === "datetime_range" ||
              type === "date" ||
              type === "date_range" ||
              type === "identification" ||
              type === "file"
            )
          }
        >
          <TextField
            label="Placeholder del campo"
            required
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Escribe un placeholder para el campo"
            maxLength={50}
          />
        </Renderify>

        <Renderify condition={type === "number"}>
          <div className="w-full">
            <div className="text-sm font-bold text-body-loud mb-2">
              Formato numérico
            </div>
            <DropdownList
              widthClassName="w-full"
              placeholder="Formato numérico"
              options={NUMERIC_FORMAT_OPTIONS.filter((opt) => {
                if (!numericFormatSearch) return true;
                const normalize = (s: string) =>
                  s
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .toLowerCase();
                const q = normalize(numericFormatSearch);
                return (
                  normalize(opt.label).includes(q) ||
                  normalize(opt.detail).includes(q) ||
                  normalize(opt.value).includes(q)
                );
              })}
              selectedValue={NUMERIC_FORMAT_OPTIONS.find(
                (o) => o.value === numberFormat,
              )}
              toggleSelection={(item) => {
                setNumberFormat(
                  item.value as "normal" | "percentage" | "usd" | "eur" | "jpy",
                );
                setNumericFormatSearch("");
              }}
              search={{
                placeholder: "Buscar formato...",
                query: numericFormatSearch,
                onSearch: setNumericFormatSearch,
              }}
            />
          </div>
        </Renderify>

        <Renderify condition={type === "number"}>
          <SelectField
            name="decimals"
            label="Decimales"
            placeholder="Tipo de documento"
            value={selectValue}
            onChange={handleChange}
            options={[
              { label: "Automático (máximo 4)", value: "auto" },
              { label: "1 decimal obligatorio", value: "1" },
              { label: "2 decimales obligatorios", value: "2" },
              { label: "3 decimales obligatorios", value: "3" },
              { label: "4 decimales obligatorios", value: "4" },
              { label: "Ninguno (0 decimales)", value: "0" },
            ]}
            required
          />
        </Renderify>

        <Renderify condition={type === "number"}>
          <GroupTextField
            label="Valor minimo y máximo"
            labelHelperText="Opcional"
            helperText="Especificar solo uno de los dos límites también es válido."
          >
            <GroupTextFieldRow>
              <Input
                placeholder="Minimo"
                type="number"
                group="left"
                value={minValue}
                className="w-full"
                onChange={(e) => setMinValue(Number(e.target.value))}
                min={0}
              />
              <Input
                placeholder="Máximo"
                type="number"
                group="right"
                className="w-full"
                value={maxValue}
                onChange={(e) => setMaxValue(Number(e.target.value))}
                min={0}
              />
            </GroupTextFieldRow>
          </GroupTextField>
        </Renderify>

        <SwitchField
          label="¿Es un campo obligatorio?"
          checked={required}
          onChange={setRequired}
          required
        />

        <SwitchField
          label="¿Se puede observar?"
          checked={preventable}
          onChange={setPreventable}
          required
        />

        <Renderify condition={type !== "select"}>
          <SwitchField
            label="¿Bloquear la edición del campo?"
            checked={isReadOnly}
            onChange={setIsReadOnly}
          />
        </Renderify>

        {/* Visibilidad section */}
        <Renderify
          condition={
            (extractFields(formData, initialField) as Variable[]).length > 0
          }
        >
          <VisibilitySection
            shouldShow={
              type === "interoperability" ||
              (extractFields(formData, initialField) as Variable[]).length > 0
            }
            variableList={extractFields(formData, initialField) as Variable[]}
            isConditional={isConditional}
            onConditionalChange={setIsConditional}
            conditions={conditions}
            onConditionsChange={setConditions}
            operator={conditionsOperator}
            onOperatorChange={setConditionsOperator}
            onValidityChange={handleValidityChange}
          />
        </Renderify>

        {/* Configuración section - dynamic based on type */}
        {type && (
          <>
            <hr className="border-neutral-quiet my-6" />
            <Heading variant="h6" className="mb-4">
              Configuración
            </Heading>

            <Renderify condition={["text", "textarea", "email"].includes(type)}>
              <TextField
                label="Número de caracteres permitidos"
                required
                placeholder="Escribe el número máximo de caracteres"
                value={configMaxLength}
                onChange={(e) => setConfigMaxLength(e.target.value)}
                type="number"
                min={1}
                error={
                  type === "number"
                    ? Number(configMaxLength) > 15
                      ? "El límite no puede ser mayor a 15"
                      : (() => {
                          // --- error si es menor a (decimales + 2) cuando decimales > 0
                          const maxLen = Number(configMaxLength);
                          if (Number.isNaN(maxLen)) return "";
                          const minChars = minCharsForPrecision(precision);
                          if (maxLen < minChars) {
                            return `El límite no puede ser menor a ${minChars}.`;
                          }
                          return "";
                        })()
                    : ""
                }
              />
            </Renderify>

            <Renderify condition={type === "text"}>
              <TextField
                label="Máscara de entrada"
                placeholder="Escribe un valor de ejemplo"
                value={mask}
                onChange={(e) => setMask(e.target.value)}
              />
            </Renderify>

            <Renderify condition={["text", "textarea"].includes(type)}>
              <DefaultValueField
                valueType={defaultValueType}
                onValueTypeChange={(type) => {
                  setDefaultValueType(type);
                  if (type === "cam") {
                    setConfigDefaultValue("");
                    // Keep variable if switching to cam? Or clear?
                    // Original logic: if "var" (now "cam"), clean value.
                    // If "inp" or "var" (new), clean variable.
                  } else {
                    setDefaultValueVariable(null);
                    // If "inp" or "var", we might keep the value if it's text.
                    // But usually switching modes clears the other field.
                  }
                }}
                inputValue={configDefaultValue}
                onInputChange={setConfigDefaultValue}
                maxLength={Number(configMaxLength)}
                selectedVariable={defaultValueVariable}
                onVariableChange={setDefaultValueVariable}
                availableVariables={extractFields(formData, initialField)}
                interoperabilityVariables={interoperabilityVariables}
                fieldType={type as FormField["type"]}
              />
            </Renderify>

            {/* =============== NUMÉRICO: Valor por defecto con validaciones de decimales ================= */}
            <Renderify condition={["number"].includes(type)}>
              <DefaultValueField
                valueType={defaultValueType}
                onValueTypeChange={(type) => {
                  setDefaultValueType(type);
                  if (type === "inp" || type === "var") {
                    // Limpiar variable si se cambia a input manual
                    setDefaultValueVariable(null);
                  } else {
                    // Si es 'cam', limpiar el valor manual
                    setConfigDefaultValue("");
                  }
                }}
                inputValue={configDefaultValue}
                onInputChange={setConfigDefaultValue}
                onInputValidate={(raw) => {
                  const maxLen = Number(configMaxLength);
                  const typingRe = buildTypingRegex(precision);

                  if (raw === "") return true;
                  if (!typingRe.test(raw)) return false;

                  const next = clampByMaxLength(
                    raw,
                    Number.isNaN(maxLen) ? undefined : maxLen,
                  );
                  return raw === next;
                }}
                isNumberField
                numberPlaceholder={
                  precision === null
                    ? "Hasta 4 decimales (opcional)"
                    : precision === 0
                      ? "Solo enteros"
                      : `Exactamente ${precision} decimales (si escribes)`
                }
                selectedVariable={defaultValueVariable}
                onVariableChange={setDefaultValueVariable}
                availableVariables={extractFields(formData, initialField)}
                errorMessage={
                  (defaultValueType === "inp" || defaultValueType === "var") &&
                  configDefaultValue
                    ? (() => {
                        const strictRe = buildStrictRegex(precision);
                        return !strictRe.test(configDefaultValue)
                          ? precision === null
                            ? "Debe ser entero o con máximo 4 decimales."
                            : precision === 0
                              ? "Debe ser un número entero."
                              : `Debe tener exactamente ${precision} decimales.`
                          : "";
                      })()
                    : ""
                }
                interoperabilityVariables={interoperabilityVariables}
                fieldType={type as FormField["type"]}
              />
            </Renderify>

            <Renderify condition={type === "select"}>
              <div>
                <div className="text-body-loud font-bold mb-2">
                  ¿De dónde provienen los valores?
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="source-variable"
                      name="source"
                      value="variable"
                      checked={configSelectSource === "database"}
                      onChange={() => setConfigSelectSource("database")}
                    />
                    <label
                      htmlFor="source-variable"
                      className="text-body text-sm"
                    >
                      Centro de datos
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Radiobutton
                      id="source-local"
                      name="source"
                      value="locale"
                      checked={configSelectSource === "manual"}
                      onChange={() => setConfigSelectSource("manual")}
                    />
                    <label htmlFor="source-local" className="text-sm text-body">
                      Locales
                    </label>
                  </div>
                </div>

                <Renderify
                  condition={configSelectSource === "manual"}
                  elseShow={
                    <div className="">
                      <div className="mt-4">
                        <span className="font-semibold text-body-loud text-sm leading-2xl">
                          Lista de selección
                        </span>
                        <div className="flex mt-1">
                          <DropdownListSubMenu
                            placeholder="Elige una variable"
                            widthClassName="flex-1"
                            options={options as DropdownListOptions}
                            submenuOptions={
                              submenuOptions as DropdownListOptions
                            }
                            selectedGroup={
                              configSelectVariable
                                ? {
                                    groupId: configSelectVariable.groupId,
                                    groupLabels:
                                      configSelectVariable.groupLabels,
                                    icon: configSelectVariable.groupIcon,
                                    items: [],
                                  }
                                : undefined
                            }
                            selectedValue={
                              configSelectVariable
                                ? {
                                    label: configSelectVariable.itemLabel,
                                    value: configSelectVariable.itemValue,
                                    icon: configSelectVariable.itemIcon,
                                  }
                                : undefined
                            }
                            toggleSelection={(item, group) => {
                              setConfigSelectSource("database");

                              // Nota: `group` viene del grupo (nodo/tabla) seleccionado
                              if (group) {
                                setConfigSelectVariable({
                                  groupId: group.groupId,
                                  groupLabels: group.groupLabels, // [nodeName] o [nodeName, tableName]
                                  groupIcon: group.icon,
                                  itemValue: String(item.value),
                                  itemLabel: String(item.label ?? item.value),
                                  itemIcon: item.icon,
                                  type: item.type || "",
                                  source: group.source,
                                });
                              } else {
                                setConfigSelectVariable({
                                  groupId: "",
                                  groupLabels: [],
                                  itemValue: String(item.value),
                                  itemLabel: String(item.label ?? item.value),
                                  itemIcon: item.icon,
                                  type: item.type || "",
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  }
                >
                  <GroupTextField
                    label="Lista local"
                    helperText="El valor generado urilizará guiones bajos en vez de espacios"
                    className="pt-4"
                  >
                    <GroupTextFieldRow>
                      <Input
                        className="w-full"
                        required
                        maxLength={100}
                        placeholder="Añade un nuevo Item"
                        value={configSelectNewItem}
                        onChange={(e) => setConfigSelectNewItem(e.target.value)}
                      />
                      <Button
                        className="mb-1 h-11 rounded-bl-none rounded-tl-none border-l-0"
                        variant="quiet"
                        onClick={handleAddSelectItem}
                      >
                        <Icon name="plus" type="far" />
                      </Button>
                    </GroupTextFieldRow>
                  </GroupTextField>

                  {configSelectItems.length > 0 && (
                    <div className="w-full max-w-2xl rounded-xl border border-neutral bg-neutral-object shadow-sm backdrop-blur p-1 mt-2">
                      <ul className="px-2">
                        {configSelectItems.map((item, index) => {
                          return (
                            <li
                              key={index}
                              className="flex items-center justify-between py-1"
                            >
                              <div className="flex flex-col">
                                <span className="text-base text-body-quiet">
                                  {item}
                                </span>
                                <span className="text-xs font-geist-mono text-body-quiet">
                                  {normalizeRef(item)}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                type="button"
                                aria-label="Eliminar"
                                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                                onClick={() => handleRemoveSelectItem(index)}
                              >
                                <Icon name="xmark" type="far" />
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </Renderify>

                <div className="py-4">
                  <SwitchField
                    label="Permitir selección múltiple"
                    checked={configSelectAllowMultiple}
                    onChange={handleToggleSelectMultiple}
                  />
                </div>

                <Renderify condition={configSelectSource === "database"}>
                  <div>
                    {configSelectSource === "database" && (
                      <SwitchField
                        isTrue="Si, crear variable por columna del registro elegido"
                        isFalse="No, generar una sola variable (arreglo)"
                        label="Expandir registro seleccionado"
                        checked={configIsVarByRegistry}
                        onChange={(value: boolean) =>
                          setConfigIsVarByRegistry(value)
                        }
                      />
                    )}
                  </div>
                </Renderify>
              </div>
            </Renderify>

            <Renderify condition={type === "datetime" || type === "date"}>
              <div className="flex gap-4">
                <div className="flex flex-col flex-1">
                  <label className="mb-1 text-sm font-medium text-body-loud">
                    Fecha mínima
                  </label>
                  <DateInput
                    onChangeDate={(date) => {
                      if (date === "now") {
                        setConfigMinDate("now");
                      } else {
                        setConfigMinDate(
                          date ? dayjs(date).format("DD/MM/YYYY") : "",
                        );
                      }
                    }}
                    value={configMinDate}
                    allowNow
                  />
                </div>
                <div className="flex flex-col flex-1">
                  <label className="mb-1 text-sm font-medium text-body-loud">
                    Fecha máxima
                  </label>
                  <DateInput
                    onChangeDate={(date) =>
                      setConfigMaxDate(
                        date ? dayjs(date).format("DD/MM/YYYY") : "",
                      )
                    }
                    value={configMaxDate}
                  />
                </div>
              </div>
              <InfoMessage
                className={
                  configMinDate &&
                  configMaxDate &&
                  configMinDate !== "now" &&
                  configMaxDate !== "now" &&
                  dayjs(configMinDate, "DD/MM/YYYY").isAfter(
                    dayjs(configMaxDate, "DD/MM/YYYY"),
                  )
                    ? "text-danger!"
                    : ""
                }
              >
                {configMinDate &&
                configMaxDate &&
                configMinDate !== "now" &&
                configMaxDate !== "now" &&
                dayjs(configMinDate, "DD/MM/YYYY").isAfter(
                  dayjs(configMaxDate, "DD/MM/YYYY"),
                )
                  ? "La fecha mínima no puede ser mayor que la fecha máxima."
                  : "Si dejas estos campos vacíos, no se aplicarán límites."}
              </InfoMessage>
              <div className="space-y-3 mt-4">
                <SwitchField
                  label="¿Incluir tiempo?"
                  checked={configIncludeTime}
                  onChange={setConfigIncludeTime}
                  required
                />
                <SwitchField
                  label="¿Formato 24h?"
                  checked={config24HourFormat}
                  onChange={() => {
                    setConfigDefaultTimeValue("");
                    setConfig24HourFormat(!config24HourFormat);
                  }}
                  required
                />
              </div>
              <DefaultValueField
                valueType={defaultValueType}
                onValueTypeChange={setDefaultValueType}
                inputValue={configDefaultValue}
                onInputChange={setConfigDefaultValue}
                selectedVariable={defaultValueVariable}
                onVariableChange={setDefaultValueVariable}
                availableVariables={extractFields(formData, initialField)}
                interoperabilityVariables={interoperabilityVariables}
                fieldType={type === "datetime" ? "datetime" : "date"}
                infoMessage="Si está vacío no se aplicará ningún valor por defecto"
              >
                <GroupTextFieldRow>
                  <DateInput
                    onChangeDate={(date) =>
                      setConfigDefaultValue(
                        date ? dayjs(date).format("DD/MM/YYYY") : "",
                      )
                    }
                    value={configDefaultValue}
                    group="right"
                    className={cn(!configIncludeTime && "rounded-r-lg")}
                  />

                  <Renderify condition={configIncludeTime}>
                    <TimeInput
                      value={configDefaultTimeValue}
                      shouldUse24={config24HourFormat}
                      onChange={(val) => setConfigDefaultTimeValue(val)}
                      group="right"
                    />
                  </Renderify>
                </GroupTextFieldRow>
              </DefaultValueField>
            </Renderify>

            <Renderify
              condition={type === "datetime_range" || type === "date_range"}
            >
              <>
                <div className="flex gap-4">
                  <div className="flex flex-col flex-1">
                    <label className="mb-1 text-sm font-medium text-body-loud">
                      Fecha mínima
                    </label>
                    <DateInput
                      onChangeDate={(date) => {
                        if (date === "now") {
                          setConfigMinDate("now");
                        } else {
                          setConfigMinDate(
                            date ? dayjs(date).format("DD/MM/YYYY") : "",
                          );
                        }
                      }}
                      value={configMinDate}
                      allowNow
                    />
                  </div>
                  <div className="flex flex-col flex-1">
                    <label className="mb-1 text-sm font-medium text-body-loud">
                      Fecha máxima
                    </label>
                    <DateInput
                      onChangeDate={(date) =>
                        setConfigMaxDate(
                          date ? dayjs(date).format("DD/MM/YYYY") : "",
                        )
                      }
                      value={configMaxDate}
                    />
                  </div>
                </div>
                <InfoMessage
                  className={
                    configMinDate &&
                    configMaxDate &&
                    configMinDate !== "now" &&
                    configMaxDate !== "now" &&
                    dayjs(configMinDate, "DD/MM/YYYY").isAfter(
                      dayjs(configMaxDate, "DD/MM/YYYY"),
                    )
                      ? "text-danger!"
                      : ""
                  }
                >
                  {configMinDate &&
                  configMaxDate &&
                  configMinDate !== "now" &&
                  configMaxDate !== "now" &&
                  dayjs(configMinDate, "DD/MM/YYYY").isAfter(
                    dayjs(configMaxDate, "DD/MM/YYYY"),
                  )
                    ? "La fecha mínima no puede ser mayor que la fecha máxima."
                    : "Si dejas estos campos vacíos, no se aplicarán límites."}
                </InfoMessage>
                <div className="space-y-3 mt-4">
                  <SwitchField
                    label="¿Incluir tiempo?"
                    checked={configIncludeTime}
                    onChange={setConfigIncludeTime}
                    required
                  />
                  <SwitchField
                    label="¿Formato 24h?"
                    checked={config24HourFormat}
                    onChange={setConfig24HourFormat}
                    required
                  />
                </div>
                <div className="flex flex-col gap-4">
                  <GroupTextField label="Valor por defecto">
                    <GroupTextFieldRow>
                      <DateInput
                        placeholder="Fecha de inicio"
                        onChangeDate={(date) =>
                          setConfigDefaultStartDate(
                            date ? dayjs(date).format("DD/MM/YYYY") : "",
                          )
                        }
                        value={configDefaultStartDate}
                      />
                      {configIncludeTime && (
                        <TimeInput
                          value={configDefaultStartTime}
                          shouldUse24={config24HourFormat}
                          onChange={(val) => setConfigDefaultStartTime(val)}
                        />
                      )}
                    </GroupTextFieldRow>
                    <GroupTextFieldRow>
                      <DateInput
                        placeholder="Fecha final"
                        onChangeDate={(date) =>
                          setConfigDefaultEndDate(
                            date ? dayjs(date).format("DD/MM/YYYY") : "",
                          )
                        }
                        value={configDefaultEndDate}
                      />
                      {configIncludeTime && (
                        <TimeInput
                          value={configDefaultEndTime}
                          shouldUse24={config24HourFormat}
                          onChange={(val) => setConfigDefaultEndTime(val)}
                        />
                      )}
                    </GroupTextFieldRow>
                  </GroupTextField>
                  <InfoMessage
                    className={
                      configDefaultStartDate &&
                      configDefaultEndDate &&
                      dayjs(configDefaultStartDate, "DD/MM/YYYY").isAfter(
                        dayjs(configDefaultEndDate, "DD/MM/YYYY"),
                      )
                        ? "text-danger!"
                        : ""
                    }
                  >
                    {configDefaultStartDate &&
                    configDefaultEndDate &&
                    dayjs(configDefaultStartDate, "DD/MM/YYYY").isAfter(
                      dayjs(configDefaultEndDate, "DD/MM/YYYY"),
                    )
                      ? "La fecha de inicio no puede ser mayor que la fecha final."
                      : "Si está vacío no se aplicará ningún valor por defecto"}
                  </InfoMessage>
                </div>
              </>
            </Renderify>

            <Renderify condition={type === "email"}>
              <div className="space-y-3">
                <GroupTextField label="Dominios permitidos" required>
                  <Input
                    className="w-full"
                    placeholder="@ejemplo.com.sv"
                    value={configAllowedDomains}
                    onChange={(e) => setConfigAllowedDomains(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (
                          configAllowedDomains.trim() &&
                          !configAllowedDomainsList.includes(
                            configAllowedDomains.trim(),
                          )
                        ) {
                          setConfigAllowedDomainsList([
                            ...configAllowedDomainsList,
                            configAllowedDomains.trim(),
                          ]);
                          setConfigAllowedDomains("");
                        }
                      }
                    }}
                  />
                  <Button
                    variant="quiet"
                    onClick={() => {
                      if (
                        configAllowedDomains.trim() &&
                        !configAllowedDomainsList.includes(
                          configAllowedDomains.trim(),
                        )
                      ) {
                        setConfigAllowedDomainsList([
                          ...configAllowedDomainsList,
                          configAllowedDomains.trim(),
                        ]);
                        setConfigAllowedDomains("");
                      }
                    }}
                    aria-label="Agregar dominio"
                  >
                    <Icon name="plus" type="far" />
                  </Button>
                </GroupTextField>
                <InfoMessage>
                  Si ninguno es añadido, todos serán permitidos
                </InfoMessage>
                {configAllowedDomainsList.length > 0 && (
                  <div className="flex flex-col gap-2 bg-neutral-object rounded-lg">
                    {configAllowedDomainsList.map((domain, idx) => (
                      <div
                        key={domain + idx}
                        className="flex items-center justify-between pl-4 py-2 rounded-lg text-base text-body hover:bg-neutral-object-accent-quiet"
                      >
                        <span>{domain}</span>
                        <Button
                          variant="ghost"
                          type="button"
                          onClick={() =>
                            setConfigAllowedDomainsList(
                              configAllowedDomainsList.filter(
                                (_, i) => i !== idx,
                              ),
                            )
                          }
                          aria-label={`Eliminar dominio ${domain}`}
                        >
                          <Icon type="far" name="close" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Dominios no permitidos */}
                <GroupTextField label="Dominios no permitidos" required>
                  <Input
                    className="w-full"
                    placeholder="@ejemplo.com.sv"
                    value={configDisallowedDomains}
                    onChange={(e) => setConfigDisallowedDomains(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (
                          configDisallowedDomains.trim() &&
                          !configDisallowedDomainsList.includes(
                            configDisallowedDomains.trim(),
                          )
                        ) {
                          setConfigDisallowedDomainsList([
                            ...configDisallowedDomainsList,
                            configDisallowedDomains.trim(),
                          ]);
                          setConfigDisallowedDomains("");
                        }
                      }
                    }}
                  />
                  <Button
                    variant="quiet"
                    onClick={() => {
                      if (
                        configDisallowedDomains.trim() &&
                        !configDisallowedDomainsList.includes(
                          configDisallowedDomains.trim(),
                        )
                      ) {
                        setConfigDisallowedDomainsList([
                          ...configDisallowedDomainsList,
                          configDisallowedDomains.trim(),
                        ]);
                        setConfigDisallowedDomains("");
                      }
                    }}
                    aria-label="Agregar dominio"
                  >
                    <Icon name="plus" type="far" />
                  </Button>
                </GroupTextField>
                <InfoMessage>
                  Si ninguno es añadido, todos serán permitidos
                </InfoMessage>
                {configDisallowedDomainsList.length > 0 && (
                  <div className="flex flex-col gap-2 bg-neutral-object rounded-lg">
                    {configDisallowedDomainsList.map((domain, idx) => (
                      <div
                        key={domain + idx}
                        className="flex items-center justify-between pl-4 py-2 rounded-lg text-base text-body hover:bg-neutral-object-accent-quiet"
                      >
                        <span>{domain}</span>
                        <Button
                          variant="ghost"
                          type="button"
                          onClick={() =>
                            setConfigDisallowedDomainsList(
                              configDisallowedDomainsList.filter(
                                (_, i) => i !== idx,
                              ),
                            )
                          }
                          aria-label={`Eliminar dominio ${domain}`}
                        >
                          <Icon type="far" name="close" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <DefaultValueField
                  valueType={defaultValueType}
                  onValueTypeChange={(type) => {
                    setDefaultValueType(type);
                    if (type === "inp") {
                      setConfigDefaultValue("");
                    } else {
                      setDefaultValueVariable(null);
                    }
                  }}
                  inputValue={configDefaultValue}
                  onInputChange={setConfigDefaultValue}
                  inputType="email"
                  maxLength={Number(configMaxLength)}
                  selectedVariable={defaultValueVariable}
                  onVariableChange={setDefaultValueVariable}
                  availableVariables={extractFields(formData, initialField)}
                  interoperabilityVariables={interoperabilityVariables}
                  fieldType={type as FormField["type"]}
                />
              </div>
            </Renderify>

            <Renderify condition={type === "phone"}>
              {/* <SwitchField
                label="¿Permitir al usuario elegir el país del número?"
                checked={configAllowCountry}
                onChange={setConfigAllowCountry}
              /> */}
              <DefaultValueField
                valueType={defaultValueType}
                onValueTypeChange={(type) => {
                  setDefaultValueType(type);
                  if (type === "inp") {
                    setConfigDefaultValue("");
                  } else {
                    setDefaultValueVariable(null);
                  }
                }}
                inputValue={configDefaultValue}
                onInputChange={setConfigDefaultValue}
                isPhoneField
                selectedVariable={defaultValueVariable}
                onVariableChange={setDefaultValueVariable}
                availableVariables={extractFields(formData, initialField)}
                interoperabilityVariables={interoperabilityVariables}
                fieldType={type as FormField["type"]}
              />
            </Renderify>

            <Renderify condition={type === "identification"}>
              <div className="space-y-3">
                <SwitchField
                  label="¿Dejar al usuario elegir el tipo de documento?"
                  checked={configAllowUserChooseDocType}
                  onChange={setConfigAllowUserChooseDocType}
                  required
                />

                <Renderify condition={!configAllowUserChooseDocType}>
                  <div className="space-y-2">
                    <SelectField
                      name="documentType"
                      label="Tipo de documento "
                      placeholder="Tipo de documento"
                      value={configDocumentType}
                      onChange={setConfigDocumentType}
                      options={[
                        { label: "DUI", value: "dui" },
                        { label: "NIT", value: "nit" },
                        { label: "Pasaporte", value: "passport_number" },
                      ]}
                      required
                    />
                  </div>
                </Renderify>

                <DefaultValueField
                  valueType={defaultValueType}
                  onValueTypeChange={(type) => {
                    setDefaultValueType(type);
                    if (type === "inp") {
                      setConfigDefaultValue("");
                    } else {
                      setDefaultValueVariable(null);
                    }
                  }}
                  inputValue={configDefaultValue}
                  onInputChange={setConfigDefaultValue}
                  selectedVariable={defaultValueVariable}
                  onVariableChange={setDefaultValueVariable}
                  availableVariables={extractFields(formData, initialField)}
                  interoperabilityVariables={interoperabilityVariables}
                  fieldType={type as FormField["type"]}
                />
              </div>
            </Renderify>

            {/* --------- Archivos ------------ */}
            <Renderify condition={type === "file"}>
              <div className="space-y-3">
                <SwitchField
                  label="¿Permitir cualquier tipo de archivo?"
                  checked={configAllowAnyFile}
                  onChange={setConfigAllowAnyFile}
                  required
                />
                <Renderify condition={!configAllowAnyFile}>
                  <div className="space-y-3">
                    <GroupTextField
                      label="Tipos de archivo permitidos"
                      required
                    >
                      <div className="flex items-center w-full relative">
                        <Input
                          group="left"
                          className="w-full"
                          placeholder="Escribe una extensión"
                          value={configAllowedFilesInput}
                          onChange={(e) => {
                            const clean = e.target.value.replace(
                              /[^a-zA-Z]/g,
                              "",
                            );
                            setConfigAllowedFilesInput(clean.toLowerCase());
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();

                              if (isValidExtension) {
                                setConfigAllowedFilesList([
                                  ...configAllowedFilesList,
                                  value,
                                ]);
                                setConfigAllowedFilesInput("");
                              }

                              return;
                            }

                            const controlKeys = [
                              "Backspace",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "ArrowUp",
                              "ArrowDown",
                              "Delete",
                              "Home",
                              "End",
                            ];
                            if (controlKeys.includes(e.key)) return;

                            if (!onlyLettersRegex.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                        />
                        <OptionTooltip title="Extensiones permitidas: pdf, doc, docx, jpg, jpeg, png, gif, webp, svg, txt, csv, xls, xlsx, ppt, pptx, json, zip, rar, mpp, mpt, dwg, dwf, dxf">
                          <div className="absolute right-4 top-3 text-body">
                            <Icon name="circle-info" type="far" />
                          </div>
                        </OptionTooltip>
                      </div>

                      <Button
                        variant="quiet"
                        disabled={!isValidExtension}
                        onClick={() => {
                          if (!isValidExtension) return;

                          setConfigAllowedFilesList([
                            ...configAllowedFilesList,
                            value,
                          ]);
                          setConfigAllowedFilesInput("");
                        }}
                        aria-label="Agregar tipo de archivo"
                      >
                        <Icon name="plus" type="far" />
                      </Button>
                    </GroupTextField>
                    <Renderify condition={configAllowedFilesList.length > 0}>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {configAllowedFilesList.map((file, idx) => (
                          <Badge
                            key={file + idx}
                            variant="action"
                            fill="ghost"
                            size="xs"
                            className="flex items-center gap-1 px-3 py-1"
                            shape="square"
                          >
                            <span>{file}</span>
                            <button
                              className="p-0 h-auto w-auto cursor-pointer"
                              onClick={() =>
                                setConfigAllowedFilesList(
                                  configAllowedFilesList.filter(
                                    (_, i) => i !== idx,
                                  ),
                                )
                              }
                            >
                              <Icon
                                type="far"
                                name="times"
                                className="text-current"
                              />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </Renderify>
                  </div>
                </Renderify>

                {/* ----------- File Size ---------- */}
                <div>
                  <label className="text-body-loud  font-semibold">
                    Tamaño máximo permitido (megabytes)
                    <span className="font-bold text-danger-quiet">*</span>
                  </label>
                  <div className="flex items-center">
                    <Button
                      onClick={() => {
                        setFileSizeKey(!fileSizeKey);
                      }}
                      size="lg"
                      variant="quiet"
                      className="rounded-tr-none rounded-br-none border-r-0 min-w-20"
                    >
                      {fileSizeKey ? "Total" : "Archivo"}
                    </Button>
                    <div className="w-full">
                      <TextField
                        label=""
                        value={maxFileSize}
                        step={1}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            setMaxFileSize(value);
                          }
                        }}
                        type="text"
                        placeholder="Ingresa el peso"
                        className="w-full rounded-tl-none rounded-bl-none"
                      />
                    </div>
                  </div>
                </div>

                <SwitchField
                  label="Subir múltiples archivos"
                  checked={configAllowMultiple}
                  onChange={setConfigAllowMultiple}
                  required
                />
              </div>
            </Renderify>

            {/* --------- Interoperabilidad ------------ */}
            <Renderify condition={type === "interoperability"}>
              <InteroperabilityConfig
                fieldName={fieldName}
                fieldKeyValue={keyValue}
                institution={configInteropInstitution}
                action={configInteropAction}
                onInstitutionChange={setConfigInteropInstitution}
                onActionChange={setConfigInteropAction}
                onParamChange={(
                  e: Array<{
                    name: string;
                    type: string;
                    label: string;
                    placeholder: string;
                  }>,
                ) => {
                  setConfigInteropParams(e);
                }}
                onReturnChange={(e) => {
                  setConfigInteropReturns(e);
                }}
              />
            </Renderify>
            <Renderify condition={type === "switch"}>
              <TextField
                label="Label"
                required
                value={configSwitchLabel}
                placeholder="Texto a mostrar"
                onChange={(e) => setConfigSwitchLabel(e.target.value)}
                type="text"
                maxLength={100}
              />

              <DefaultValueField
                valueType={defaultValueType}
                onValueTypeChange={setDefaultValueType}
                inputValue={configDefaultValue}
                onInputChange={(value) => {
                  // Only allow "true" or "false" for switch fields
                  if (value === "true" || value === "false" || value === "") {
                    setConfigDefaultValue(value);
                  }
                }}
                selectedVariable={defaultValueVariable}
                onVariableChange={setDefaultValueVariable}
                availableVariables={extractFields(formData, initialField)}
                interoperabilityVariables={interoperabilityVariables}
                infoMessage="Si está vacío no se aplicará ningún valor por defecto"
                fieldType="switch"
              >
                <GroupTextFieldRow>
                  <div className="bg-neutral-object-accent-silent border border-neutral rounded-r-md w-full pt-2 px-2">
                    <Switch
                      checked={configDefaultValue === "true"}
                      onCheckedChange={(checked) =>
                        setConfigDefaultValue(checked ? "true" : "false")
                      }
                    />{" "}
                    <span className="ml-2 text-body-loud">
                      {configDefaultValue === "true" ? "Verdadero" : "Falso"}
                    </span>
                  </div>
                </GroupTextFieldRow>
              </DefaultValueField>
            </Renderify>
          </>
        )}
      </div>
    </ScrollModal>
  );
};

export default FieldConfigModal;
